import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/services/api";

// ─── EVENTOS FAVORITOS ────────────────────────────────────────────────────────

export const getFavoritosUsuario = createAsyncThunk(
  "eventosFavoritos/getFavoritosUsuario",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/evento-favorito-usuario");
      return Array.isArray(data) ? data : (data.eventos || []);
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.response?.data?.message || "Error al obtener favoritos"
      );
    }
  }
);

export const getFavoritosByUsuarioUid = createAsyncThunk(
  "eventosFavoritos/getFavoritosByUsuarioUid",
  async (uid, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/evento-favorito-usuario/by-usuario/${uid}`);
      return Array.isArray(data) ? data : (data.eventos || []);
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.response?.data?.message || "Error al obtener favoritos del usuario"
      );
    }
  }
);

export const agregarFavorito = createAsyncThunk(
  "eventosFavoritos/agregarFavorito",
  async (eventoUid, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/evento-favorito-usuario", { eventoUid });
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.response?.data?.message || "Error al agregar favorito"
      );
    }
  }
);

export const eliminarFavorito = createAsyncThunk(
  "eventosFavoritos/eliminarFavorito",
  async (eventoUid, { rejectWithValue }) => {
    try {
      await api.delete(`/evento-favorito-usuario/${eventoUid}`);
      return eventoUid;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.response?.data?.message || "Error al eliminar favorito"
      );
    }
  }
);

// ─── EXPERIENCIAS FAVORITAS ───────────────────────────────────────────────────

export const getFavoritasExperiencias = createAsyncThunk(
  "eventosFavoritos/getFavoritasExperiencias",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/experiencia-favorita-usuario");
      return Array.isArray(data) ? data : (data.experiencias || []);
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error al obtener experiencias favoritas"
      );
    }
  }
);

export const getFavoritasExperienciasByUid = createAsyncThunk(
  "eventosFavoritos/getFavoritasExperienciasByUid",
  async (uid, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/experiencia-favorita-usuario/by-usuario/${uid}`);
      return Array.isArray(data) ? data : (data.experiencias || []);
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error al obtener experiencias favoritas del usuario"
      );
    }
  }
);

export const agregarExperienciaFavorita = createAsyncThunk(
  "eventosFavoritos/agregarExperienciaFavorita",
  async ({ slug, titulo, imagen }, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/experiencia-favorita-usuario", { slug, titulo, imagen });
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error al agregar experiencia favorita"
      );
    }
  }
);

export const eliminarExperienciaFavorita = createAsyncThunk(
  "eventosFavoritos/eliminarExperienciaFavorita",
  async (slug, { rejectWithValue }) => {
    try {
      await api.delete(`/experiencia-favorita-usuario/${encodeURIComponent(slug)}`);
      return slug;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Error al eliminar experiencia favorita"
      );
    }
  }
);

// ─── SLICE ────────────────────────────────────────────────────────────────────

const eventosFavoritosSlice = createSlice({
  name: "eventosFavoritos",
  initialState: {
    favoritos: [],
    favoritosPublicos: [],
    favoritasExperiencias: [],
    favoritasExperienciasPublicas: [],
    isLoading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // getFavoritosUsuario
      .addCase(getFavoritosUsuario.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getFavoritosUsuario.fulfilled, (state, action) => {
        state.isLoading = false;
        state.favoritos = action.payload;
      })
      .addCase(getFavoritosUsuario.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // getFavoritosByUsuarioUid
      .addCase(getFavoritosByUsuarioUid.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getFavoritosByUsuarioUid.fulfilled, (state, action) => {
        state.isLoading = false;
        state.favoritosPublicos = action.payload;
      })
      .addCase(getFavoritosByUsuarioUid.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // agregarFavorito
      .addCase(agregarFavorito.fulfilled, (_state, _action) => {})
      // eliminarFavorito
      .addCase(eliminarFavorito.fulfilled, (state, action) => {
        state.favoritos = state.favoritos.filter(
          (f) => f.uid !== action.payload && f.eventoUid !== action.payload
        );
      })
      // getFavoritasExperiencias
      .addCase(getFavoritasExperiencias.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getFavoritasExperiencias.fulfilled, (state, action) => {
        state.isLoading = false;
        state.favoritasExperiencias = action.payload;
      })
      .addCase(getFavoritasExperiencias.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // getFavoritasExperienciasByUid
      .addCase(getFavoritasExperienciasByUid.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getFavoritasExperienciasByUid.fulfilled, (state, action) => {
        state.isLoading = false;
        state.favoritasExperienciasPublicas = action.payload;
      })
      .addCase(getFavoritasExperienciasByUid.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // agregarExperienciaFavorita
      .addCase(agregarExperienciaFavorita.fulfilled, (state, action) => {
        const { slug, titulo, imagen } = action.meta.arg;
        const ya = state.favoritasExperiencias.some((e) => e.slug === slug);
        if (!ya) state.favoritasExperiencias.unshift({ slug, titulo, imagen });
      })
      // eliminarExperienciaFavorita
      .addCase(eliminarExperienciaFavorita.fulfilled, (state, action) => {
        state.favoritasExperiencias = state.favoritasExperiencias.filter(
          (e) => e.slug !== action.payload
        );
      });
  },
});

export default eventosFavoritosSlice.reducer;
